import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { auditApi } from "../lib/auditApi";
import { auth } from "../lib/api";

// ─── Severity + labels ───────────────────────────────────────────────────
const ACTION_META = {
    "admin.login":              { severity: "info",     label: "Login super-admin",       icon: "fa-right-to-bracket" },
    "admin.login_failed":       { severity: "warning",  label: "Tentativa de login",      icon: "fa-triangle-exclamation" },
    "tenant.created":           { severity: "success",  label: "Tenant criado",           icon: "fa-store" },
    "tenant.suspended":         { severity: "critical", label: "Tenant suspenso",         icon: "fa-ban" },
    "tenant.activated":         { severity: "success",  label: "Tenant ativado",          icon: "fa-power-off" },
    "tenant.deleted":           { severity: "critical", label: "Tenant excluído",         icon: "fa-trash-can" },
    "tenant.restored":          { severity: "success",  label: "Tenant reabilitado",      icon: "fa-rotate-left" },
    "tenant.owner_password_reset": { severity: "warning", label: "Senha do dono resetada", icon: "fa-key" },
    "user.login":               { severity: "info",     label: "Login do tenant",         icon: "fa-right-to-bracket" },
    "legal.terms_updated":      { severity: "info",     label: "Termos de Uso atualizados", icon: "fa-file-contract" },
    "seo.updated":              { severity: "info",     label: "SEO atualizado",          icon: "fa-magnifying-glass-chart" },
    "staff.login":              { severity: "info",     label: "Login de funcionário",    icon: "fa-right-to-bracket" },
    "staff.created":            { severity: "success",  label: "Funcionário criado",      icon: "fa-user-plus" },
    "staff.updated":            { severity: "info",     label: "Funcionário atualizado",  icon: "fa-user-pen" },
    "staff.deactivated":        { severity: "critical", label: "Funcionário desativado",  icon: "fa-user-slash" },
    "staff.time_record_added":  { severity: "info",     label: "Ponto registrado",        icon: "fa-clock" },
    "tenant.payment_registered":{ severity: "success",  label: "Pagamento registrado",    icon: "fa-money-bill" },
    "affiliate.created":        { severity: "info",     label: "Afiliado criado",         icon: "fa-user-plus" },
    "affiliate.requested":      { severity: "info",     label: "Solicitação de afiliado", icon: "fa-user-clock" },
    "affiliate.approved":       { severity: "success",  label: "Afiliado aprovado",       icon: "fa-user-check" },
    "affiliate.suspended":      { severity: "critical", label: "Afiliado suspenso",       icon: "fa-user-slash" },
    "commission.paid":          { severity: "success",  label: "Comissão paga",           icon: "fa-hand-holding-dollar" },
    "settings.updated":         { severity: "info",     label: "Configurações alteradas", icon: "fa-sliders" },
    "domain.created":           { severity: "info",     label: "Domínio cadastrado",      icon: "fa-globe" },
    "domain.verified":          { severity: "success",  label: "Domínio verificado",      icon: "fa-shield-check" },
    "domain.verify_failed":     { severity: "warning",  label: "Falha na verificação",    icon: "fa-shield-halved" },
    "domain.deleted":           { severity: "critical", label: "Domínio removido",        icon: "fa-trash-can" },
    "whatsapp.disconnected":    { severity: "warning",  label: "WhatsApp desconectado",   icon: "fa-plug-circle-xmark" },
    "booking.confirmed":        { severity: "info",     label: "Agendamento confirmado",  icon: "fa-check" },
    "booking.refused":          { severity: "warning",  label: "Agendamento recusado",    icon: "fa-xmark" },
};

const SEVERITY_STYLE = {
    critical: { chip: "bg-red-500/15 text-red-200 border-red-500/40",      dot: "bg-red-400",     ring: "ring-red-500/30" },
    warning:  { chip: "bg-amber-500/15 text-amber-200 border-amber-500/40", dot: "bg-amber-400",  ring: "ring-amber-500/30" },
    success:  { chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40", dot: "bg-emerald-400", ring: "ring-emerald-500/30" },
    info:     { chip: "bg-sky-500/15 text-sky-200 border-sky-500/40",     dot: "bg-sky-400",     ring: "ring-sky-500/30" },
};

const ACTOR_STYLE = {
    admin:  { chip: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30", icon: "fa-shield-halved", label: "Super-Admin" },
    tenant: { chip: "bg-violet-500/15 text-violet-200 border-violet-500/30", icon: "fa-store",         label: "Tenant" },
    system: { chip: "bg-slate-500/15 text-slate-300 border-slate-500/30",   icon: "fa-robot",         label: "Sistema" },
};

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatLogMessage = (log) => {
    const d = log.details || {};
    const shop = d.shop_name ? ` · ${d.shop_name}` : "";
    switch (log.action) {
        case "admin.login":            return `${log.actor_name} entrou no Painel Mestre`;
        case "admin.login_failed":     return `Tentativa falhada: ${d.email || "e-mail desconhecido"}`;
        case "tenant.created":         return `Novo tenant "${d.shop_name || "?"}" (${(d.plan || "?").toUpperCase()}) via ${d.signup || "?"}`;
        case "tenant.suspended":       return `Tenant suspenso${shop}`;
        case "tenant.activated":       return `Tenant reativado${shop}`;
        case "tenant.deleted":         return `Tenant excluído (soft delete)${shop}`;
        case "tenant.restored":        return `Tenant reabilitado${shop}`;
        case "tenant.owner_password_reset": return `Senha resetada para ${d.owner_email || "o dono"}${shop}`;
        case "user.login":             return `${log.actor_name} fez login`;
        case "legal.terms_updated":    return `Termos de Uso atualizados para a versão ${d.version}`;
        case "seo.updated":            return `SEO & Meta atualizado por ${log.actor_name}`;
        case "staff.login":            return `${log.actor_name} (funcionário) fez login`;
        case "staff.created":          return `Funcionário "${d.name || "?"}" criado`;
        case "staff.updated":          return `Funcionário "${d.name || "?"}" atualizado`;
        case "staff.deactivated":      return `Funcionário "${d.name || "?"}" desativado`;
        case "staff.time_record_added": return `Ponto registrado para ${d.date || "?"}`;
        case "tenant.payment_registered": return `Pagamento ${brl(d.amount)} registrado${shop}${d.commission_generated ? " · comissão gerada" : ""}`;
        case "affiliate.created":      return `Afiliado "${d.name}" criado (${d.ref_code})`;
        case "affiliate.requested":    return `Nova solicitação: ${d.name} (${d.email})`;
        case "affiliate.approved":     return `Afiliado ${d.name || d.ref_code} aprovado`;
        case "affiliate.suspended":    return `Afiliado ${d.name || d.ref_code} suspenso`;
        case "commission.paid":        return `Comissão ${brl(d.amount)} paga para ${d.affiliate_name || "?"}`;
        case "settings.updated":       return `Comissões: 1ª ${d.commission_first}% · 2ª ${d.commission_second}% · rec ${d.commission_recurring}%`;
        case "domain.created":         return `Domínio ${d.domain} cadastrado`;
        case "domain.verified":        return `Domínio ${d.domain} verificado`;
        case "domain.verify_failed":   return `Falha ao verificar ${d.domain}`;
        case "domain.deleted":         return `Domínio ${d.domain} removido`;
        case "whatsapp.disconnected":  return `WhatsApp desconectado${shop}`;
        case "booking.confirmed":      return `Agendamento confirmado${shop}`;
        case "booking.refused":        return `Agendamento recusado${d.reason ? ` (${d.reason})` : ""}`;
        default:                       return log.action.replace(/\./g, " ").replace(/_/g, " ");
    }
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtRelative = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60)      return "agora";
    if (diff < 3600)    return `${Math.floor(diff / 60)} min atrás`;
    if (diff < 86400)   return `${Math.floor(diff / 3600)}h atrás`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d atrás`;
    return `${Math.floor(diff / 2592000)}m atrás`;
};

// ─── KPI Card ────────────────────────────────────────────────────────────
const KPI = ({ icon, label, value, tone = "indigo", testid }) => {
    const tones = {
        indigo: "from-indigo-500/20 to-indigo-500/5 border-indigo-500/25 text-indigo-200",
        red:    "from-red-500/20 to-red-500/5 border-red-500/25 text-red-200",
        amber:  "from-amber-500/20 to-amber-500/5 border-amber-500/25 text-amber-200",
        emerald:"from-emerald-500/20 to-emerald-500/5 border-emerald-500/25 text-emerald-200",
        sky:    "from-sky-500/20 to-sky-500/5 border-sky-500/25 text-sky-200",
    };
    return (
        <div data-testid={testid} className={`rounded-2xl bg-gradient-to-br ${tones[tone]} border p-5 min-h-[120px]`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-slate-400">
                <i className={`fa-solid ${icon}`}></i>
                <span>{label}</span>
            </div>
            <div className="mt-3 salon-heading text-4xl text-white">{value}</div>
        </div>
    );
};

// ─── Main ────────────────────────────────────────────────────────────────
const AdminAuditLogs = () => {
    const [summary, setSummary] = useState(null);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 50 });
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ action: "all", actor_type: "all", search: "" });

    const loadSummary = useCallback(async () => {
        try {
            const s = await auditApi.summary();
            setSummary(s);
        } catch (e) {
            toast.error("Falha ao carregar resumo", { description: e.message });
        }
    }, []);

    const loadLogs = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const r = await auditApi.list({
                action: filters.action, actor_type: filters.actor_type,
                search: filters.search || undefined, page, limit: 50,
            });
            setLogs(r.logs || []);
            setPagination({ page: r.page, pages: r.pages, total: r.total, limit: r.limit });
        } catch (e) {
            toast.error("Falha ao carregar logs", { description: e.message });
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { loadSummary(); }, [loadSummary]);
    useEffect(() => { loadLogs(1); }, [loadLogs]);

    const actionOptions = useMemo(() => {
        const known = Object.keys(ACTION_META);
        const fromSummary = (summary?.by_action || []).map((a) => a.action);
        return Array.from(new Set([...known, ...fromSummary])).sort();
    }, [summary]);

    const handleExport = async () => {
        try {
            const url = auditApi.exportCsvUrl({ action: filters.action, actor_type: filters.actor_type });
            const token = auth.getAdminToken() || auth.getToken();
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `kinng-audit-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success("CSV exportado");
        } catch (e) {
            toast.error("Falha ao exportar", { description: e.message });
        }
    };

    return (
        <div data-testid="admin-audit-logs" className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Segurança & Compliance</div>
                    <h2 className="salon-heading text-2xl text-white">Logs de Auditoria</h2>
                    <p className="text-xs text-slate-500 mt-1">Timeline de todas as ações críticas da plataforma nos últimos 30 dias.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="audit-refresh-btn"
                        onClick={() => { loadSummary(); loadLogs(pagination.page); }}
                        className="h-10 px-4 rounded-xl border border-white/10 hover:border-indigo-500/40 text-slate-300 hover:text-white text-xs uppercase tracking-[0.2em] transition-all"
                    >
                        <i className="fa-solid fa-arrows-rotate mr-2"></i>Atualizar
                    </button>
                    <button
                        data-testid="audit-export-btn"
                        onClick={handleExport}
                        className="h-10 px-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-200 text-xs uppercase tracking-[0.2em] transition-all"
                    >
                        <i className="fa-solid fa-file-csv mr-2"></i>Exportar CSV
                    </button>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KPI testid="audit-kpi-total"   icon="fa-database"           label="Total 30d"    value={summary?.total ?? "—"}       tone="indigo" />
                <KPI testid="audit-kpi-today"   icon="fa-clock"              label="Hoje"         value={summary?.today ?? "—"}       tone="sky" />
                <KPI testid="audit-kpi-critical"icon="fa-triangle-exclamation" label="Críticos"    value={summary?.critical ?? "—"}    tone="red" />
                <KPI testid="audit-kpi-logins"  icon="fa-right-to-bracket"   label="Logins admin" value={summary?.logins ?? "—"}      tone="amber" />
                <KPI testid="audit-kpi-newtenants" icon="fa-store"           label="Novos tenants" value={summary?.new_tenants ?? "—"} tone="emerald" />
            </div>

            {/* Filters */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
                <div className="flex-1 flex items-center gap-2 bg-slate-950/60 rounded-xl border border-white/5 px-3">
                    <i className="fa-solid fa-magnifying-glass text-slate-500 text-sm"></i>
                    <input
                        data-testid="audit-search"
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") loadLogs(1); }}
                        placeholder="Buscar por ator ou ação..."
                        className="bg-transparent flex-1 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 outline-none"
                    />
                </div>
                <select
                    data-testid="audit-filter-action"
                    value={filters.action}
                    onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
                    className="bg-slate-950/60 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-200 min-w-[200px]"
                >
                    <option value="all">Todas as ações</option>
                    {actionOptions.map((a) => (
                        <option key={a} value={a}>{ACTION_META[a]?.label || a}</option>
                    ))}
                </select>
                <select
                    data-testid="audit-filter-actor"
                    value={filters.actor_type}
                    onChange={(e) => setFilters((f) => ({ ...f, actor_type: e.target.value }))}
                    className="bg-slate-950/60 border border-white/5 rounded-xl px-3 py-2.5 text-sm text-slate-200 min-w-[160px]"
                >
                    <option value="all">Todos os atores</option>
                    <option value="admin">Super-Admin</option>
                    <option value="tenant">Tenant</option>
                    <option value="system">Sistema</option>
                </select>
            </div>

            {/* Timeline */}
            <div data-testid="audit-timeline" className="rounded-2xl border border-white/5 bg-slate-900/30 overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Timeline</div>
                        <div className="text-sm text-slate-300 mt-0.5">
                            {loading ? "Carregando..." : `${pagination.total} evento${pagination.total !== 1 ? "s" : ""}`}
                        </div>
                    </div>
                    <div className="text-[11px] text-slate-500">Pág. {pagination.page} de {pagination.pages || 1}</div>
                </div>

                {logs.length === 0 && !loading ? (
                    <div className="p-10 text-center text-slate-500 text-sm">
                        <i className="fa-solid fa-inbox text-4xl mb-3 block text-slate-700"></i>
                        Nenhum log encontrado com esses filtros.
                    </div>
                ) : (
                    <div className="divide-y divide-white/5">
                        {logs.map((log) => {
                            const meta = ACTION_META[log.action] || { severity: "info", label: log.action, icon: "fa-circle-info" };
                            const sev = SEVERITY_STYLE[meta.severity];
                            const actor = ACTOR_STYLE[log.actor_type] || ACTOR_STYLE.system;
                            return (
                                <div
                                    key={log.id}
                                    data-testid={`audit-log-${log.id}`}
                                    className="px-5 py-4 flex items-start gap-4 hover:bg-white/[0.02] transition-colors"
                                >
                                    {/* Icon */}
                                    <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center border ring-1 ${sev.chip} ${sev.ring}`}>
                                        <i className={`fa-solid ${meta.icon}`}></i>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span data-testid="audit-log-action-chip" className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${sev.chip}`}>
                                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${sev.dot}`}></span>
                                                {meta.label}
                                            </span>
                                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${actor.chip}`}>
                                                <i className={`fa-solid ${actor.icon} text-[9px]`}></i>
                                                {actor.label}
                                            </span>
                                            {log.tenant_id && log.details?.shop_name && (
                                                <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-white/10 text-slate-400">
                                                    <i className="fa-solid fa-building text-[9px] mr-1"></i>
                                                    {log.details.shop_name}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1.5 text-sm text-white leading-relaxed">
                                            {formatLogMessage(log)}
                                        </div>
                                        <div className="mt-1 text-[11px] text-slate-500">
                                            por <span className="text-slate-300">{log.actor_name || "Sistema"}</span>
                                            <span className="mx-2">·</span>
                                            {fmtDateTime(log.created_at)}
                                            <span className="mx-2">·</span>
                                            <span className="text-slate-400">{fmtRelative(log.created_at)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between text-xs">
                        <button
                            data-testid="audit-prev-page"
                            disabled={pagination.page <= 1}
                            onClick={() => loadLogs(pagination.page - 1)}
                            className="px-3 py-1.5 rounded-lg border border-white/10 hover:border-indigo-500/40 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300"
                        >
                            <i className="fa-solid fa-chevron-left mr-1"></i>Anterior
                        </button>
                        <div className="text-slate-500">
                            {(pagination.page - 1) * pagination.limit + 1}–
                            {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                        </div>
                        <button
                            data-testid="audit-next-page"
                            disabled={pagination.page >= pagination.pages}
                            onClick={() => loadLogs(pagination.page + 1)}
                            className="px-3 py-1.5 rounded-lg border border-white/10 hover:border-indigo-500/40 disabled:opacity-30 disabled:cursor-not-allowed text-slate-300"
                        >
                            Próximo<i className="fa-solid fa-chevron-right ml-1"></i>
                        </button>
                    </div>
                )}
            </div>

            {/* Breakdown by tenant */}
            {summary?.by_tenant?.length > 0 && (
                <div className="rounded-2xl border border-white/5 bg-slate-900/30 p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Top tenants por atividade (30d)</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {summary.by_tenant.slice(0, 8).map((t) => (
                            <div key={t.tenant_id} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                                <div className="text-sm text-slate-200 truncate">{t.shop_name || t.tenant_id?.slice(0, 8)}</div>
                                <div className="text-xs text-indigo-300 font-mono">{t.count} eventos</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAuditLogs;
